<template>
  <div class="v1-screen">
    <div class="v1-screen__content">
      <h1
        v-if="title"
        class="v1-screen__title"
        :class="{ 'v1-screen__title--center': titleAlign === 'center' }"
        data-testid="v1-screen-title"
      >
        {{ title }}
      </h1>
      <div v-if="$slots.subtitle" class="v1-screen__subtitle">
        <slot name="subtitle" />
      </div>
      <slot />
    </div>
    <div class="v1-screen__dock">
      <Button
        variant="primary"
        size="lg"
        full-width
        :disabled="nextDisabled"
        :loading="nextLoading"
        data-testid="v1-screen-next"
        @click="$emit('next')"
      >
        {{ nextLabel || t("continue") }}
      </Button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import { Button } from "@payjoy/design-system";

/**
 * Shared layout for the v1 payment screens: an optional heading, an optional
 * 16px subtitle slot, the screen body, and a docked primary "Continue" action.
 * The heading + docked button styling is centralized here so each screen only
 * supplies its content and wires `next-disabled` / `@next`.
 */
export default defineComponent({
  name: "V1ScreenScaffold",
  components: { Button },
  props: {
    title: { type: String, default: "" },
    titleAlign: { type: String as PropType<"left" | "center">, default: "left" },
    nextLabel: { type: String, default: "" },
    nextDisabled: { type: Boolean, default: false },
    nextLoading: { type: Boolean, default: false },
  },
  emits: ["next"],
  setup() {
    const { t } = useI18n();
    return { t };
  },
});
</script>

<style scoped>
.v1-screen__content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 16px 96px;
  box-sizing: border-box;
  /* Fill the webview so screens can bottom-anchor content above the docked button. */
  min-height: 100vh;
  min-height: 100dvh;
}

.v1-screen__title {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.v1-screen__title--center {
  text-align: center;
}

.v1-screen__subtitle {
  font-family: "Inter", sans-serif;
  font-size: 16px;
  color: var(--pj-color-brand-gray-550, #74756f);
}

.v1-screen__dock {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px;
  /* Figma redesign (PYMT-2288, node 1257-16173): the primary CTA is dark
     (#222926 / Gray-07), not the DS default green. Override the DS
     action-primary tokens for the docked button; they cascade into it. */
  --pj-color-action-primary-bg-default: var(--pj-color-brand-gray-850, #222926);
  --pj-color-action-primary-bg-hover: var(--pj-color-brand-gray-650, #3f4340);
  --pj-color-action-primary-bg-pressed: #171b1d;
  --pj-color-action-primary-text-default: var(--pj-color-brand-white, #ffffff);
}

@media (min-width: 768px) {
  .v1-screen__content {
    min-height: 0;
    padding: 24px 0;
  }

  .v1-screen__title,
  .v1-screen__subtitle {
    text-align: center;
  }

  .v1-screen__dock {
    position: static;
    padding: 16px 0 24px;
  }
}
</style>
